// Per-page dynamic colour ordering. Every page gets a --color-dynamic-1..5
// sequence derived from its colorTheme (the fixed first slot) and a seeded
// shuffle of the rest, so two pages with the same theme still differ. Applied
// as inline vars on .contentContainer by PageTransition.
//
// The defaults these override live in _tqs_color.scss (:root).

export const DYNAMIC_COLORS = [
  'golden-meadow',
  'forest-thistle',
  'morning-sky',
  'soft-moss',
  'spring-dawn',
];

function hashStr(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (Math.imul(31, hash) + str.charCodeAt(i)) | 0;
  }
  return hash;
}

function seededRandom(seed) {
  let s = seed;
  return () => {
    s |= 0;
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function colorVarsFromOrder(ordered) {
  const vars = {};
  ordered.forEach((colorName, i) => {
    vars[`--color-dynamic-${i + 1}`] = `var(--color-${colorName})`;
  });
  return vars;
}

// The homepage always uses this fixed order (yellow, green, orange, purple,
// blue) rather than a seeded shuffle. It used to inherit it from the :root
// defaults, but those are now all the theme white — every page sets its own
// order inline so nothing paints in a colour before this runs.
export const HOME_COLOR_ORDER = [
  'golden-meadow',
  'soft-moss',
  'spring-dawn',
  'forest-thistle',
  'morning-sky',
];

// The component demo page (/jtb-components) also uses a fixed order rather
// than a seeded shuffle, so the blocks always demo in the same sequence:
// purple, green, blue, yellow, orange.
export const DEMO_COLOR_ORDER = [
  'forest-thistle',
  'soft-moss',
  'morning-sky',
  'golden-meadow',
  'spring-dawn',
];

export function getDynamicColorVars(colorTheme, slug = '', isHome = false) {
  if (isHome) return colorVarsFromOrder(HOME_COLOR_ORDER);
  if (typeof slug === 'string' && slug.includes('jtb-components')) {
    return colorVarsFromOrder(DEMO_COLOR_ORDER);
  }
  const themeIndex = colorTheme ? DYNAMIC_COLORS.indexOf(colorTheme) : 0;
  return getDynamicColorVarsByIndex(themeIndex === -1 ? 0 : themeIndex, slug);
}

export function getDynamicColorVarsByIndex(fixedIndex = 0, slug = '') {
  const rest = DYNAMIC_COLORS.filter((_, i) => i !== fixedIndex);

  // Seeded Fisher-Yates
  const rand = seededRandom(hashStr(slug));
  for (let i = rest.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [rest[i], rest[j]] = [rest[j], rest[i]];
  }

  const ordered = [DYNAMIC_COLORS[fixedIndex], ...rest];
  return colorVarsFromOrder(ordered);
}
